import React from "react";

const statusStyles = {
  available: {
    label: "Available",
    wrapper:
      "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    dot: "bg-emerald-500",
  },
  unavailable: {
    label: "Unavailable",
    wrapper:
      "bg-red-50 text-red-700 ring-red-600/20",
    dot: "bg-red-500",
  },
  partial: {
    label: "Partial",
    wrapper:
      "bg-amber-50 text-amber-700 ring-amber-600/20",
    dot: "bg-amber-500",
  },
  warning: {
    label: "Warning",
    wrapper:
      "bg-amber-50 text-amber-700 ring-amber-600/20",
    dot: "bg-amber-500",
  },
  checking: {
    label: "Checking",
    wrapper:
      "bg-blue-50 text-blue-700 ring-blue-600/20",
    dot: "bg-blue-500",
  },
};

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 011.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function StatusBadge({ status }) {
  const normalizedStatus =
    String(status || "available")
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

  const config =
    statusStyles[normalizedStatus] ||
    statusStyles.available;

  const icon =
    normalizedStatus === "available" ? (
      <CheckIcon />
    ) : normalizedStatus === "unavailable" ? (
      <XIcon />
    ) : (
      <WarningIcon />
    );

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ring-1 ring-inset ${config.wrapper}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${config.dot}`}
      />
      {icon}
      {config.label}
    </span>
  );
}

function AvailabilityBar({
  available = 0,
  required = 0,
}) {
  const safeRequired = Math.max(
    Number(required) || 0,
    0
  );

  const safeAvailable = Math.max(
    Number(available) || 0,
    0
  );

  const percentage =
    safeRequired > 0
      ? Math.min(
          (safeAvailable / safeRequired) * 100,
          100
        )
      : safeAvailable > 0
      ? 100
      : 0;

  const sufficient =
    safeAvailable >= safeRequired;

  return (
    <div className="min-w-28">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-700">
          {safeAvailable}
        </span>

        <span className="text-[10px] text-gray-400">
          / {safeRequired}
        </span>
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full transition-all ${
            sufficient
              ? "bg-emerald-500"
              : "bg-red-500"
          }`}
          style={{
            width: `${percentage}%`,
          }}
        />
      </div>
    </div>
  );
}

export default function BlueprintAvailabilityTable({
  availability = [],
  data = [],
  rows = [],
  loading = false,
  error = "",
  onRefresh,
  onRowClick,
  disabled = false,
  title = "Question Availability",
  description = "Review whether the question pool can satisfy the blueprint requirements.",
  emptyMessage = "No availability data available.",
  showStatus = true,
  showAvailability = true,
  showShortage = true,
  showPercentage = false,
  compact = false,
}) {
  const sourceRows =
    availability.length > 0
      ? availability
      : data.length > 0
      ? data
      : rows;

  const getName = (row, index) =>
    row.name ||
    row.title ||
    row.label ||
    row.chapterName ||
    row.topicName ||
    `Selection ${index + 1}`;

  const getRequired = (row) =>
    Number(
      row.required ??
        row.requiredCount ??
        row.selectionCount ??
        row.requested ??
        row.requestedCount ??
        0
    );

  const getAvailable = (row) =>
    Number(
      row.available ??
        row.availableCount ??
        row.questionCount ??
        row.totalAvailable ??
        0
    );

  const getShortage = (row) => {
    if (
      row.shortage !== undefined &&
      row.shortage !== null
    ) {
      return Math.max(
        Number(row.shortage) || 0,
        0
      );
    }

    return Math.max(
      getRequired(row) -
        getAvailable(row),
      0
    );
  };

  const getStatus = (row) => {
    if (row.status) {
      return String(row.status)
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
    }

    const required = getRequired(row);
    const available = getAvailable(row);

    if (available >= required) {
      return "available";
    }

    if (available > 0) {
      return "partial";
    }

    return "unavailable";
  };

  const totalRequired = sourceRows.reduce(
    (total, row) =>
      total + getRequired(row),
    0
  );

  const totalAvailable = sourceRows.reduce(
    (total, row) =>
      total + getAvailable(row),
    0
  );

  const totalShortage = sourceRows.reduce(
    (total, row) =>
      total + getShortage(row),
    0
  );

  const overallAvailable =
    totalAvailable >= totalRequired;

  if (loading) {
    return (
      <div className="w-full rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-4">
          <div className="h-4 w-44 animate-pulse rounded bg-gray-200" />
          <div className="mt-2 h-3 w-72 animate-pulse rounded bg-gray-100" />
        </div>

        <div className="space-y-3 p-4">
          {[1, 2, 3, 4].map((item) => (
            <div
              key={item}
              className="flex items-center gap-4"
            >
              <div className="h-8 flex-1 animate-pulse rounded bg-gray-100" />
              <div className="h-8 w-24 animate-pulse rounded bg-gray-100" />
              <div className="h-8 w-20 animate-pulse rounded bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {title}
          </h3>

          {description && (
            <p className="mt-1 text-xs leading-5 text-gray-500">
              {description}
            </p>
          )}
        </div>

        {onRefresh && (
          <button
            type="button"
            disabled={disabled || loading}
            onClick={onRefresh}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 8A6.5 6.5 0 105.1 14.2"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 4.5V8h-3.5"
              />
            </svg>
            Refresh
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
          {error}
        </div>
      )}

      {sourceRows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-10 text-center">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-white text-gray-400 shadow-sm ring-1 ring-gray-200">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                d="M5 5h14M5 12h14M5 19h14"
              />
            </svg>
          </div>

          <p className="mt-3 text-sm font-medium text-gray-700">
            {emptyMessage}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Selection
                    </th>

                    {showAvailability && (
                      <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Available / Required
                      </th>
                    )}

                    {showShortage && (
                      <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Shortage
                      </th>
                    )}

                    {showPercentage && (
                      <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Coverage
                      </th>
                    )}

                    {showStatus && (
                      <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Status
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {sourceRows.map(
                    (row, index) => {
                      const required =
                        getRequired(row);

                      const available =
                        getAvailable(row);

                      const shortage =
                        getShortage(row);

                      const status =
                        getStatus(row);

                      const coverage =
                        required > 0
                          ? Math.min(
                              (available /
                                required) *
                                100,
                              100
                            )
                          : 100;

                      const clickable =
                        Boolean(
                          onRowClick
                        );

                      return (
                        <tr
                          key={
                            row.id ??
                            row._id ??
                            row.key ??
                            `availability-${index}`
                          }
                          onClick={
                            clickable
                              ? () =>
                                  onRowClick(
                                    row,
                                    index
                                  )
                              : undefined
                          }
                          className={
                            clickable
                              ? "cursor-pointer transition hover:bg-gray-50"
                              : ""
                          }
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div
                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                                  status ===
                                  "available"
                                    ? "bg-emerald-50 text-emerald-600"
                                    : status ===
                                      "unavailable"
                                    ? "bg-red-50 text-red-600"
                                    : "bg-amber-50 text-amber-600"
                                }`}
                              >
                                {index + 1}
                              </div>

                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-gray-800">
                                  {getName(
                                    row,
                                    index
                                  )}
                                </p>

                                {(row.type ||
                                  row.chapter ||
                                  row.topic) && (
                                  <p className="mt-0.5 truncate text-xs text-gray-500">
                                    {row.type ||
                                      row.chapter ||
                                      row.topic}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          {showAvailability && (
                            <td className="px-4 py-3">
                              <AvailabilityBar
                                available={
                                  available
                                }
                                required={
                                  required
                                }
                              />
                            </td>
                          )}

                          {showShortage && (
                            <td className="px-4 py-3">
                              <span
                                className={`text-sm font-semibold ${
                                  shortage > 0
                                    ? "text-red-600"
                                    : "text-emerald-600"
                                }`}
                              >
                                {shortage > 0
                                  ? shortage
                                  : "—"}
                              </span>
                            </td>
                          )}

                          {showPercentage && (
                            <td className="px-4 py-3">
                              <span
                                className={`text-sm font-semibold ${
                                  coverage >= 100
                                    ? "text-emerald-600"
                                    : "text-amber-600"
                                }`}
                              >
                                {coverage.toFixed(
                                  0
                                )}
                                %
                              </span>
                            </td>
                          )}

                          {showStatus && (
                            <td className="px-4 py-3 text-right">
                              <StatusBadge
                                status={
                                  status
                                }
                              />
                            </td>
                          )}
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-gray-100 md:hidden">
              {sourceRows.map(
                (row, index) => {
                  const required =
                    getRequired(row);

                  const available =
                    getAvailable(row);

                  const shortage =
                    getShortage(row);

                  const status =
                    getStatus(row);

                  const coverage =
                    required > 0
                      ? Math.min(
                          (available /
                            required) *
                            100,
                          100
                        )
                      : 100;

                  return (
                    <button
                      key={
                        row.id ??
                        row._id ??
                        row.key ??
                        `mobile-${index}`
                      }
                      type="button"
                      disabled={
                        disabled ||
                        !onRowClick
                      }
                      onClick={() =>
                        onRowClick?.(
                          row,
                          index
                        )
                      }
                      className={`w-full p-4 text-left ${
                        onRowClick
                          ? "transition hover:bg-gray-50"
                          : "cursor-default"
                      } disabled:cursor-default`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                              status ===
                              "available"
                                ? "bg-emerald-50 text-emerald-600"
                                : status ===
                                  "unavailable"
                                ? "bg-red-50 text-red-600"
                                : "bg-amber-50 text-amber-600"
                            }`}
                          >
                            {index + 1}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-800">
                              {getName(
                                row,
                                index
                              )}
                            </p>

                            {row.type && (
                              <p className="mt-0.5 text-xs text-gray-500">
                                {row.type}
                              </p>
                            )}
                          </div>
                        </div>

                        {showStatus && (
                          <StatusBadge
                            status={status}
                          />
                        )}
                      </div>

                      {showAvailability && (
                        <div className="mt-4">
                          <div className="mb-1.5 flex items-center justify-between text-xs">
                            <span className="text-gray-500">
                              Availability
                            </span>

                            <span className="font-medium text-gray-700">
                              {available} /{" "}
                              {required}
                            </span>
                          </div>

                          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                            <div
                              className={`h-full rounded-full ${
                                available >=
                                required
                                  ? "bg-emerald-500"
                                  : "bg-red-500"
                              }`}
                              style={{
                                width: `${
                                  coverage
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="mt-3 flex items-center justify-between">
                        {showShortage && (
                          <span className="text-xs text-gray-500">
                            Shortage:{" "}
                            <strong
                              className={
                                shortage > 0
                                  ? "text-red-600"
                                  : "text-emerald-600"
                              }
                            >
                              {shortage}
                            </strong>
                          </span>
                        )}

                        {showPercentage && (
                          <span className="text-xs font-medium text-gray-500">
                            {coverage.toFixed(
                              0
                            )}
                            % covered
                          </span>
                        )}
                      </div>
                    </button>
                  );
                }
              )}
            </div>
          </div>

          <div
            className={`mt-3 flex flex-col gap-2 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${
              overallAvailable
                ? "border-emerald-200 bg-emerald-50"
                : "border-red-200 bg-red-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  overallAvailable
                    ? "bg-emerald-100 text-emerald-600"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {overallAvailable ? (
                  <CheckIcon />
                ) : (
                  <XIcon />
                )}
              </div>

              <div>
                <p
                  className={`text-sm font-semibold ${
                    overallAvailable
                      ? "text-emerald-900"
                      : "text-red-900"
                  }`}
                >
                  {overallAvailable
                    ? "Blueprint can be satisfied"
                    : "Insufficient question availability"}
                </p>

                <p
                  className={`mt-0.5 text-xs ${
                    overallAvailable
                      ? "text-emerald-700"
                      : "text-red-700"
                  }`}
                >
                  {overallAvailable
                    ? `${totalAvailable} available questions satisfy ${totalRequired} required selections.`
                    : `${totalShortage} additional question${
                        totalShortage === 1
                          ? ""
                          : "s"
                      } required to satisfy the blueprint.`}
                </p>
              </div>
            </div>

            <div className="text-left sm:text-right">
              <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                Coverage
              </p>

              <p
                className={`text-lg font-bold ${
                  overallAvailable
                    ? "text-emerald-700"
                    : "text-red-700"
                }`}
              >
                {totalRequired > 0
                  ? Math.min(
                      (totalAvailable /
                        totalRequired) *
                        100,
                      100
                    ).toFixed(0)
                  : 100}
                %
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}